import type {
  SharedSideNavLabels,
  SideNavItem,
} from '@evisort/eds/components/AppLayout/subcomponents/SideNav/types';

import { SideNav } from '@evisort/eds/components/AppLayout/subcomponents/SideNav/SideNav';
import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAskAnything } from '~/features/ask-anything/ask-anything-button/useAskAnything';
import { useCandu } from '~/features/onboarding/Candu/useCandu';
import { CLMEvolutionFeedbackModal } from '~/features/workflows-clm-evolution/feedback/CLMEvolutionFeedbackModal';
import { messages as feedbackMessages } from '~/features/workflows-clm-evolution/feedback/messages';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { useSidebarNav } from './SidebarNavContext';
import {
  ASK_AI_KEY,
  FEEDBACK_KEY,
  getRegionAttributes,
  resolveActiveKey,
  toSideNavItem,
  toSideNavLink,
} from './sideNavAdapter';
import { useAppNavBrand } from './useAppNavBrand';
import { useNavigationItems } from './useNavigationItems';
import { useNavLinkActivate } from './useNavLinkActivate';
import { useSidebarPinnedLinks } from './useSidebarPinnedLinks';

const messages = defineMessages({
  expandSection: {
    id: 'AppSidebarNav.expandSection',
    defaultMessage: 'Expand {label} section',
    description:
      'Accessible label for the chevron that expands a sidebar nav section.',
  },
  collapseSection: {
    id: 'AppSidebarNav.collapseSection',
    defaultMessage: 'Collapse {label} section',
    description:
      'Accessible label for the chevron that collapses a sidebar nav section.',
  },
  sidebarLandmark: {
    id: 'AppSidebarNav.sidebarLandmark',
    defaultMessage: 'sidebar navigation',
    description: 'Accessible name for the sidebar navigation landmark.',
  },
  mainNavLandmark: {
    id: 'AppSidebarNav.mainNavLandmark',
    defaultMessage: 'main navigation',
    description: 'Accessible name for the primary nav list inside the sidebar.',
  },
  workspaceLabel: {
    id: 'AppSidebarNav.workspaceLabel',
    defaultMessage: 'Workspace',
    description: 'Overline label above the sidebar nav sections.',
  },
  showSidebar: {
    id: 'AppSidebarNav.showSidebar',
    defaultMessage: 'Show sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is collapsed.',
  },
  hideSidebar: {
    id: 'AppSidebarNav.hideSidebar',
    defaultMessage: 'Hide sidebar',
    description:
      'Tooltip and a11y label for the sidebar toggle when the sidebar is expanded.',
  },
  askAi: {
    id: 'AppSidebarNav.askAi',
    defaultMessage: 'Ask AI',
    description: 'Label for the sidebar entry that opens the AI chatbot.',
  },
  pinnedHeading: {
    id: 'AppSidebarNav.pinnedHeading',
    defaultMessage: 'Pinned',
    description: 'Overline above the pinned entries at the top of the sidebar.',
  },
  pinnedCount: {
    id: 'AppSidebarNav.pinnedCount',
    defaultMessage: '{count} pinned entries',
    description: 'Accessible label for the count of pinned sidebar entries.',
  },
  reorderPinnedItem: {
    id: 'AppSidebarNav.reorderPinnedItem',
    defaultMessage: 'Reorder {label}',
    description:
      'Accessible label for the handle that drags a pinned entry to a new position.',
  },
  pinItem: {
    id: 'AppSidebarNav.pinItem',
    defaultMessage: 'Pin {label}',
    description:
      'Accessible label for the button that pins a sidebar nav entry.',
  },
  unpinItem: {
    id: 'AppSidebarNav.unpinItem',
    defaultMessage: 'Unpin {label}',
    description:
      'Accessible label for the button that unpins a sidebar nav entry.',
  },
});

type Navigation = ReturnType<typeof useNavigationItems>;

interface AppSidebarNavViewProps {
  navigation: Navigation;
  onNavLinkActivate: () => void;
  /** Opens the AI chatbot. Unset hides the Ask AI entry. */
  onAskAi?: () => void;
}

function AppSidebarNavView({
  navigation,
  onAskAi,
  onNavLinkActivate,
}: AppSidebarNavViewProps) {
  const intl = useIntl();
  const { location } = useRouting();
  const { isCollapsed, isMobile, toggleCollapsed } = useSidebarNav();
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const { adminLink, homeLink, items } = navigation;
  const { pinnedLinks, reorderPinned, togglePin } =
    useSidebarPinnedLinks(items);

  const labels = useMemo<SharedSideNavLabels>(
    () => ({
      collapseSection: (label) =>
        intl.formatMessage(messages.collapseSection, { label }),
      expandSection: (label) =>
        intl.formatMessage(messages.expandSection, { label }),
      hideSidebar: intl.formatMessage(messages.hideSidebar),
      navigation: intl.formatMessage(messages.mainNavLandmark),
      pin: (label) => intl.formatMessage(messages.pinItem, { label }),
      pinned: intl.formatMessage(messages.pinnedHeading),
      pinnedCount: (count) =>
        intl.formatMessage(messages.pinnedCount, { count }),
      reorder: (label) =>
        intl.formatMessage(messages.reorderPinnedItem, { label }),
      showSidebar: intl.formatMessage(messages.showSidebar),
      sidebar: intl.formatMessage(messages.sidebarLandmark),
      unpin: (label) => intl.formatMessage(messages.unpinItem, { label }),
      workspace: intl.formatMessage(messages.workspaceLabel),
    }),
    [intl],
  );

  const primaryItems = useMemo<SideNavItem[]>(
    () => [
      ...(homeLink
        ? [
            toSideNavItem(homeLink, {
              icon: 'nav-home',
              testId: 'sidebar-nav-home',
            }),
          ]
        : []),
      ...(onAskAi
        ? [
            toSideNavItem(
              {
                label: intl.formatMessage(messages.askAi),
                pathname: ASK_AI_KEY,
              },
              {
                icon: 'evisort-ai-static',
                iconVariant: 'ai',
                isAction: true,
                testId: 'sidebar-nav-ask-ai',
              },
            ),
          ]
        : []),
    ],
    [homeLink, intl, onAskAi],
  );

  const sections = useMemo(
    () => items.map((link) => toSideNavItem(link)),
    [items],
  );

  const footerItems = useMemo<SideNavItem[]>(
    () => [
      ...(hasClmEvolutionPhase1
        ? [
            toSideNavItem(
              {
                label: intl.formatMessage(feedbackMessages.open),
                pathname: FEEDBACK_KEY,
              },
              {
                analyticsSection: 'footer',
                icon: 'comment',
                isAction: true,
                testId: 'sidebar-nav-feedback',
              },
            ),
          ]
        : []),
      ...(adminLink
        ? [
            toSideNavItem(adminLink, {
              analyticsSection: 'footer',
              testId: 'sidebar-nav-admin',
            }),
          ]
        : []),
    ],
    [adminLink, hasClmEvolutionPhase1, intl],
  );

  const pinned = useMemo(
    () => pinnedLinks.map((link) => toSideNavLink(link)),
    [pinnedLinks],
  );

  const activeKey = useMemo(
    () =>
      resolveActiveKey(location.pathname, [
        ...(homeLink ? [homeLink] : []),
        ...items,
        ...(adminLink ? [adminLink] : []),
      ]),
    [adminLink, homeLink, items, location.pathname],
  );

  const regionAttributes = useMemo(
    () => getRegionAttributes(isCollapsed),
    [isCollapsed],
  );

  const brand = useAppNavBrand('sidebar-mobile-brand-button');

  const handleActivate = useCallback(
    (key: string) => {
      if (key === ASK_AI_KEY) {
        onAskAi?.();
        if (isMobile && !isCollapsed) toggleCollapsed();
        return;
      }
      if (key === FEEDBACK_KEY) {
        setIsFeedbackVisible(true);
        return;
      }
      onNavLinkActivate();
    },
    [isCollapsed, isMobile, onAskAi, onNavLinkActivate, toggleCollapsed],
  );

  return (
    <>
      <SideNav
        activeKey={activeKey}
        brand={brand}
        collapsed={!isMobile && isCollapsed}
        footerItems={footerItems}
        items={sections}
        labels={labels}
        pinned={pinned}
        primaryItems={primaryItems}
        regionAttributes={regionAttributes}
        onActivate={handleActivate}
        onReorderPinned={reorderPinned}
        onToggleCollapsed={toggleCollapsed}
        onTogglePin={togglePin}
      />
      {hasClmEvolutionPhase1 && (
        <CLMEvolutionFeedbackModal
          isVisible={isFeedbackVisible}
          onHide={() => setIsFeedbackVisible(false)}
        />
      )}
    </>
  );
}

function AppSidebarNavWithAskAi(
  props: Omit<AppSidebarNavViewProps, 'onAskAi'>,
) {
  const { handleToggleChatbot } = useAskAnything();

  return <AppSidebarNavView {...props} onAskAi={handleToggleChatbot} />;
}

export function AppSidebarNav() {
  const user = useCurrentUser();
  const { location } = useRouting();

  useCandu(user);

  const onNavLinkActivate = useNavLinkActivate();

  const navigation = useNavigationItems({ onNavLinkClick: onNavLinkActivate });

  if (
    [
      RoutePathType.Login,
      RoutePathType.LoginEmbed,
      RoutePathType.LoginEmbedSuccess,
    ].includes(location.pathname as RoutePathType)
  ) {
    return null;
  }

  if (navigation.showAskAi) {
    return (
      <AppSidebarNavWithAskAi
        navigation={navigation}
        onNavLinkActivate={onNavLinkActivate}
      />
    );
  }

  return (
    <AppSidebarNavView
      navigation={navigation}
      onNavLinkActivate={onNavLinkActivate}
    />
  );
}
